/**
 * Credit Service for Blog Generation
 * Manages user credits based on AOX token holdings
 */
import { API_BASE_URL } from './config';

export interface CreditInfo {
  success: boolean;
  credits: number;
  dailyCreditAllowance: number;
  aoxBalance: number;
  lastCreditUpdate: string;
  totalCreditsEarned: number;
  totalCreditsSpent: number;
}

export class CreditService {
  /**
   * Get credit information for a wallet
   */
  async getCredits(walletAddress: string): Promise<CreditInfo> {
    try {
      const response = await fetch(`${API_BASE_URL}/credits/${walletAddress}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching credits:', error);
      throw new Error('Failed to fetch credits');
    }
  }

  /**
   * Update credits based on current AOX balance
   */
  async updateCredits(walletAddress: string, aoxBalance: number): Promise<CreditInfo> {
    try {
      const response = await fetch(`${API_BASE_URL}/credits/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          walletAddress,
          aoxBalance
        })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating credits:', error);
      throw new Error('Failed to update credits');
    }
  }

  /**
   * Spend credits for blog generation
   */
  async spendCredits(walletAddress: string, amount: number = 1): Promise<{ success: boolean; remainingCredits: number; error?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/credits/spend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          walletAddress,
          amount
        })
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          remainingCredits: data.credits || 0,
          error: data.error
        };
      }

      return {
        success: true,
        remainingCredits: data.remainingCredits
      };
    } catch (error) {
      console.error('Error spending credits:', error);
      return {
        success: false,
        remainingCredits: 0,
        error: 'Failed to spend credits'
      };
    }
  }

  /**
   * Check if user has enough credits
   */
  async hasEnoughCredits(walletAddress: string, required: number = 1): Promise<boolean> {
    try {
      const creditInfo = await this.getCredits(walletAddress);
      return creditInfo.credits >= required;
    } catch (error) {
      console.error('Error checking credits:', error);
      return false;
    }
  }

  /**
   * Format credit display text
   */
  formatCreditDisplay(credits: number, dailyAllowance: number): string {
    return `${credits} credit${credits !== 1 ? 's' : ''} (${dailyAllowance}/day)`;
  }
}

export const creditService = new CreditService();
